/**
 * DocuSeal sign-off (MB's R120; decisions 133, 158, 129; ADR-016; docs/specs/cloud/docuseal.md): the crew sign the
 * JSP safety meeting (R119) and later the JSA (R13) through the organisation's own DocuSeal account. Teslyn Field makes
 * the PDF with DocuSeal's text tags on its sign-off page and sends it to Teslyn Cloud's `docuseal` Edge Function, which
 * creates the submission with the organisation's key (kept in Vault, never on a device), follows DocuSeal's
 * notifications and keeps the signed PDF.
 *
 * DocuSeal's API as documented on 27 Oct 2026 (www.docuseal.com/docs/api, console.docuseal.com/openapi.yml):
 * `X-Auth-Token` header; `POST /submissions/pdf`, `GET /submissions/{id}`, `GET /submissions/{id}/documents`,
 * `DELETE /submissions/{id}`; the form.* and submission.* webhooks. Their documented example answers are the tests'
 * fixtures (`tests/fixtures/docuseal/`).
 *
 * This module has no imports: the Edge Function uses an identical copy (`supabase/functions/_shared/docuseal.ts`,
 * written by `tools/dev/copy-edge-shared.mjs`; `test/docuseal.test.ts` fails while they differ).
 */

/** Teslyn Cloud's Edge Function. */
export const DOCUSEAL_FUNCTION = 'docuseal';
/** DocuSeal's global server (decision 158: Thomson is on DocuSeal's cloud plan). */
export const DOCUSEAL_API = 'https://api.docuseal.com';
/** The header DocuSeal sends the webhook's secret key-value pair in (DocuSeal, Settings, Webhooks, Security). */
export const DOCUSEAL_WEBHOOK_HEADER = 'x-teslyn-webhook-secret';
/** The largest PDF sent for signing. */
export const SIGNOFF_PDF_MAX_BYTES = 20 * 1024 * 1024;
/** The largest signed PDF or audit log kept. */
export const SIGNOFF_SIGNED_MAX_BYTES = 50 * 1024 * 1024;
/** Signers of one request, at most (a JSP meeting's attendees, a JSA's crew and managers). */
export const SIGNOFF_SIGNERS_MAX = 40;
/** A request still "sending" after this long was interrupted: a new send may begin. */
export const SIGNOFF_SENDING_STALE_MS = 10 * 60 * 1000;

/** The documents signed through DocuSeal: the JSP safety meeting (R119) and the JSA (R13, decision 837). */
export const SIGNOFF_DOCUMENT_KINDS = ['jsp_meeting', 'jsa'] as const;
export type SignoffDocumentKind = (typeof SIGNOFF_DOCUMENT_KINDS)[number];

export const SIGNOFF_DOCUMENT_WORDS: Readonly<Record<SignoffDocumentKind, string>> = {
  jsp_meeting: 'JSP safety meeting',
  jsa: 'JSA',
};

/** What a signer is asked to confirm, by document (the email DocuSeal sends). */
const SIGNOFF_ASKS: Readonly<Record<SignoffDocumentKind, string>> = {
  jsp_meeting: 'you were at the meeting and know the risks and mitigations it lists',
  jsa: 'confirm you have read and understood the survey safety information and the risk assessment',
};

/** The address DocuSeal sends its notifications to (DocuSeal, Settings, Webhooks). */
export const docusealWebhookUrl = (supabaseUrl: string): string =>
  `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${DOCUSEAL_FUNCTION}/webhook`;

/** The address of one of the function's requests. */
export const docusealFunctionUrl = (
  supabaseUrl: string,
  path: 'send' | 'file' | 'refresh' | 'withdraw' | 'approval-sign' | 'approval-refresh' | 'approval-withdraw',
): string => `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${DOCUSEAL_FUNCTION}/${path}`;

// ------------------------------------------------------------------------------------------------ text tags

/** The DocuSeal role of the nth signer (1-based): no spaces, so a tag never breaks over two lines. */
export const docusealRole = (n: number): string => `Attendee${n}`;

/**
 * The text tags of the nth signer's row of a sign-off page (DocuSeal, "Use embedded text field tags"): the signature
 * in the Signed cell and the signing date (read only, Australian order) in the Dated cell. DocuSeal removes the tags
 * from the document it shows (`remove_tags` defaults to true); the PDF prints them in white 4 pt text.
 */
export function docusealSignatureTags(n: number): { readonly signature: string; readonly date: string } {
  if (!Number.isInteger(n) || n < 1 || n > SIGNOFF_SIGNERS_MAX)
    throw new Error(`A sign-off page has 1 to ${SIGNOFF_SIGNERS_MAX} signers.`);
  const role = docusealRole(n);
  return {
    signature: `{{Signature${n};type=signature;role=${role};width=150;height=26}}`,
    date: `{{Date${n};type=datenow;role=${role};format=DD/MM/YYYY;width=80;height=16}}`,
  };
}

// ------------------------------------------------------------------------------------------------ routes

export type DocusealRoute =
  | { readonly kind: 'send' }
  | { readonly kind: 'file' }
  | { readonly kind: 'refresh' }
  | { readonly kind: 'withdraw' }
  | { readonly kind: 'webhook' }
  /** A client admin signs a contract sent for approval (R191): the signing address, made once. */
  | { readonly kind: 'approval-sign' }
  /** Ask DocuSeal for a contract approval's signing state now. */
  | { readonly kind: 'approval-refresh' }
  /** Staff withdraw a contract approval whose signing has begun: DocuSeal archives the submission first. */
  | { readonly kind: 'approval-withdraw' }
  | { readonly kind: 'unknown' };

/** The route of a request to the `docuseal` function (with or without `/functions/v1`). */
export function docusealRoute(method: string, pathname: string): DocusealRoute {
  const at = pathname.indexOf(`/${DOCUSEAL_FUNCTION}`);
  if (at < 0) return { kind: 'unknown' };
  const rest = pathname.slice(at + DOCUSEAL_FUNCTION.length + 1).replace(/\/+$/, '');
  const m = method.toUpperCase();
  if (m === 'GET' && rest === '/file') return { kind: 'file' };
  if (m !== 'POST') return { kind: 'unknown' };
  switch (rest) {
    case '/send':
      return { kind: 'send' };
    case '/refresh':
      return { kind: 'refresh' };
    case '/withdraw':
      return { kind: 'withdraw' };
    case '/webhook':
      return { kind: 'webhook' };
    case '/approval-sign':
      return { kind: 'approval-sign' };
    case '/approval-refresh':
      return { kind: 'approval-refresh' };
    case '/approval-withdraw':
      return { kind: 'approval-withdraw' };
    default:
      return { kind: 'unknown' };
  }
}

// ------------------------------------------------------------------------------------------------ send

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX64 = /^[0-9a-f]{64}$/;

export interface SignoffSendRequest {
  readonly projectId: string;
  readonly documentKind: SignoffDocumentKind;
  readonly documentId: string;
  readonly documentRevision: number;
  readonly title: string;
  readonly fileName: string;
  readonly pdfBase64: string;
  readonly pdfSha256: string;
  readonly signers: readonly { readonly personId: string }[];
}

export type SignoffParsed<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: string };

/** The bytes a base64 text holds (without decoding it). */
export function base64Bytes(b64: string): number {
  const s = b64.replace(/\s+/g, '');
  const pad = s.endsWith('==') ? 2 : s.endsWith('=') ? 1 : 0;
  return Math.floor((s.length * 3) / 4) - pad;
}

/** Check the body Teslyn Field posts to `/docuseal/send`. The error is in words, never echoing the document. */
export function parseSignoffSend(body: unknown): SignoffParsed<SignoffSendRequest> {
  const b = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const str = (k: string): string => (typeof b[k] === 'string' ? (b[k] as string).trim() : '');
  const projectId = str('project_id').toLowerCase();
  if (!UUID.test(projectId)) return { ok: false, error: 'Say which project (project_id).' };
  const kind = str('document_kind');
  if (!(SIGNOFF_DOCUMENT_KINDS as readonly string[]).includes(kind))
    return { ok: false, error: `document_kind must be one of: ${SIGNOFF_DOCUMENT_KINDS.join(', ')}.` };
  const documentId = str('document_id').toLowerCase();
  if (!UUID.test(documentId)) return { ok: false, error: 'Say which document (document_id).' };
  const rev = b['document_revision'];
  if (typeof rev !== 'number' || !Number.isInteger(rev) || rev < 1 || rev > 10_000)
    return { ok: false, error: 'document_revision must be the PDF revision (1 or more).' };
  const title = str('title');
  if (!title || title.length > 200) return { ok: false, error: 'The title is 1 to 200 characters.' };
  const fileName = str('file_name');
  if (!/^[^\\/:*?"<>|]{1,200}\.pdf$/i.test(fileName))
    return { ok: false, error: 'file_name must be the PDF file name (ending .pdf).' };
  const pdfBase64 = typeof b['pdf_base64'] === 'string' ? (b['pdf_base64'] as string) : '';
  if (!pdfBase64 || !/^[A-Za-z0-9+/=\s]+$/.test(pdfBase64))
    return { ok: false, error: 'pdf_base64 must be the PDF in base64.' };
  if (base64Bytes(pdfBase64) > SIGNOFF_PDF_MAX_BYTES)
    return { ok: false, error: `The PDF is larger than ${SIGNOFF_PDF_MAX_BYTES / 1024 / 1024} MB.` };
  if (!pdfBase64.replace(/\s+/g, '').startsWith('JVBERi'))
    return { ok: false, error: 'The file sent is not a PDF.' };
  const pdfSha256 = str('pdf_sha256').toLowerCase();
  if (!HEX64.test(pdfSha256)) return { ok: false, error: 'pdf_sha256 must be the PDF’s SHA-256 (hex).' };
  const raw = Array.isArray(b['signers']) ? (b['signers'] as unknown[]) : [];
  if (raw.length < 1 || raw.length > SIGNOFF_SIGNERS_MAX)
    return { ok: false, error: `Send 1 to ${SIGNOFF_SIGNERS_MAX} signers.` };
  const signers: { personId: string }[] = [];
  for (const s of raw) {
    const id =
      s && typeof s === 'object' && typeof (s as { person_id?: unknown }).person_id === 'string'
        ? (s as { person_id: string }).person_id.trim().toLowerCase()
        : '';
    if (!UUID.test(id))
      return { ok: false, error: 'Every signer is a person of the staff register (person_id).' };
    if (signers.some((x) => x.personId === id)) return { ok: false, error: 'A signer is listed twice.' };
    signers.push({ personId: id });
  }
  return {
    ok: true,
    value: {
      projectId,
      documentKind: kind as SignoffDocumentKind,
      documentId,
      documentRevision: rev,
      title,
      fileName,
      pdfBase64: pdfBase64.replace(/\s+/g, ''),
      pdfSha256,
      signers,
    },
  };
}

/** A signer as the database resolved them from the staff register (with the email DocuSeal sends to). */
export interface ResolvedSigner {
  readonly person_id: string;
  readonly name: string;
  readonly email: string;
}

/** The body of DocuSeal's `POST /submissions/pdf`: everyone is asked at once, the tags place the fields. */
export function docusealSubmissionBody(a: {
  readonly title: string;
  readonly fileName: string;
  readonly pdfBase64: string;
  readonly organisation: string;
  readonly signers: readonly ResolvedSigner[];
  /** The document signed (the JSP meeting when not given): it picks what the email asks. */
  readonly documentKind?: SignoffDocumentKind;
}): Record<string, unknown> {
  return {
    name: a.title,
    send_email: true,
    order: 'random',
    documents: [{ name: a.fileName.replace(/\.pdf$/i, ''), file: a.pdfBase64 }],
    submitters: a.signers.map((s, i) => ({
      role: docusealRole(i + 1),
      name: s.name,
      email: s.email,
      external_id: s.person_id,
    })),
    message: {
      subject: `${a.organisation}: please sign ${a.title}`,
      body:
        `${a.organisation} asks you to sign ${a.title}: ${SIGNOFF_ASKS[a.documentKind ?? 'jsp_meeting']}.` +
        `\n\nOpen the document to read and sign it: {{submitter.link}}`,
    },
  };
}

// ------------------------------------------------------------------------------------------------ contracts (R191)

/**
 * The DocuSeal role of the client who signs a contract from the client portal (R191, decision 1010 item 2). The
 * contract's signing page carries this role's text tags on the client's lines (`CONTRACT_SIGNING_TAGS`).
 */
export const CONTRACT_SIGNER_ROLE = 'Client';

/**
 * The DocuSeal role of the operator's manager who signs second (decision 1014 item 4 B: "will be managers"), on the
 * operator's lines (`CONTRACT_OPERATOR_TAGS`). The witness lines of both parties stay for pen and paper.
 */
export const CONTRACT_COUNTERSIGNER_ROLE = 'Operator';

/**
 * The text tags on the client's lines of the contract's signing page, in the order of its fields (Signed, Print Name,
 * Position, WITNESS, Signed, Print Name, Date): the client's signature, name and position, then the date of signing
 * on the last line. The witness lines get none. White 4 pt text, so a printed copy shows empty lines.
 */
export const CONTRACT_SIGNING_TAGS: readonly (string | null)[] = [
  `{{ClientSignature;type=signature;role=${CONTRACT_SIGNER_ROLE};width=160;height=22}}`,
  `{{ClientName;type=text;role=${CONTRACT_SIGNER_ROLE};width=200;height=14}}`,
  `{{ClientPosition;type=text;role=${CONTRACT_SIGNER_ROLE};width=200;height=14}}`,
  null,
  null,
  null,
  `{{ClientDate;type=datenow;role=${CONTRACT_SIGNER_ROLE};format=DD/MM/YYYY;width=80;height=14}}`,
];

/** The same on the operator's lines, for the manager who signs second (decision 1014). */
export const CONTRACT_OPERATOR_TAGS: readonly (string | null)[] = [
  `{{OperatorSignature;type=signature;role=${CONTRACT_COUNTERSIGNER_ROLE};width=160;height=22}}`,
  `{{OperatorName;type=text;role=${CONTRACT_COUNTERSIGNER_ROLE};width=200;height=14}}`,
  `{{OperatorPosition;type=text;role=${CONTRACT_COUNTERSIGNER_ROLE};width=200;height=14}}`,
  null,
  null,
  null,
  `{{OperatorDate;type=datenow;role=${CONTRACT_COUNTERSIGNER_ROLE};format=DD/MM/YYYY;width=80;height=14}}`,
];

/** The client admin who signs, as the database resolved them (their sign-in's name and email). */
export interface ApprovalSigner {
  readonly user_id: string;
  readonly name: string;
  readonly email: string;
}

/**
 * The body of `POST /submissions/pdf` for a contract the client signs from the portal. With the operator's manager
 * (decision 1014): two signers in order, the client admin first (no email from DocuSeal: they open the signing page
 * from the portal), then the manager, whom DocuSeal emails once the client has signed; each one's name filled in.
 * Without one (R191 before decision 1014), the client alone.
 */
export function docusealContractSubmissionBody(a: {
  readonly title: string;
  readonly fileName: string;
  readonly pdfBase64: string;
  readonly signer: ApprovalSigner;
  readonly manager?: ApprovalSigner | null;
  /** The operator's name, for DocuSeal's email to the manager. */
  readonly organisation?: string;
}): Record<string, unknown> {
  const client = {
    role: CONTRACT_SIGNER_ROLE,
    name: a.signer.name,
    email: a.signer.email,
    external_id: a.signer.user_id,
    values: { ClientName: a.signer.name },
  };
  const documents = [{ name: a.fileName.replace(/\.pdf$/i, ''), file: a.pdfBase64 }];
  if (!a.manager)
    return { name: a.title, send_email: false, order: 'preserved', documents, submitters: [client] };
  const who = a.organisation ? `${a.organisation}: ` : '';
  return {
    name: a.title,
    send_email: true,
    order: 'preserved',
    documents,
    submitters: [
      { ...client, send_email: false },
      {
        role: CONTRACT_COUNTERSIGNER_ROLE,
        name: a.manager.name,
        email: a.manager.email,
        external_id: a.manager.user_id,
        values: { OperatorName: a.manager.name },
      },
    ],
    message: {
      subject: `${who}please sign ${a.title}`,
      body:
        `The client has signed ${a.title}. Please sign it for ${a.organisation || 'the operator'}.` +
        `\n\nOpen the contract to read and sign it: {{submitter.link}}`,
    },
  };
}

/**
 * The signing address DocuSeal answers for one signer (`embed_src` of the submitter: the client by default, or the
 * manager with `CONTRACT_COUNTERSIGNER_ROLE`), only DocuSeal's own.
 */
export function submissionSignUrl(
  answer: unknown,
  externalId: string,
  role: string = CONTRACT_SIGNER_ROLE,
): string | null {
  const subs: unknown[] = Array.isArray(answer)
    ? answer
    : answer && typeof answer === 'object' && Array.isArray((answer as { submitters?: unknown }).submitters)
      ? (answer as { submitters: unknown[] }).submitters
      : [];
  const s = subs.find(
    (x): x is Record<string, unknown> =>
      !!x &&
      typeof x === 'object' &&
      ((x as Record<string, unknown>)['external_id'] === externalId ||
        (x as Record<string, unknown>)['role'] === role),
  );
  const u = s ? s['embed_src'] : null;
  return typeof u === 'string' && docusealFileUrlAllowed(u) ? u : null;
}

/** Check the body of the approval routes: `{ request }`. */
export function parseApprovalRequestBody(body: unknown): SignoffParsed<{ readonly request: string }> {
  const id =
    body && typeof body === 'object' && typeof (body as { request?: unknown }).request === 'string'
      ? (body as { request: string }).request.trim().toLowerCase()
      : '';
  return UUID.test(id)
    ? { ok: true, value: { request: id } }
    : { ok: false, error: 'Say which approval (request).' };
}

// ------------------------------------------------------------------------------------------------ states

/** A request's state (`signoff_request.state`). */
export type SignoffState = 'sending' | 'sent' | 'completed' | 'declined' | 'expired' | 'withdrawn' | 'failed';
export const SIGNOFF_STATES: readonly SignoffState[] = [
  'sending',
  'sent',
  'completed',
  'declined',
  'expired',
  'withdrawn',
  'failed',
];
/** A signer's state: DocuSeal's submitter status. */
export type SignerStatus = 'awaiting' | 'sent' | 'opened' | 'completed' | 'declined';

/** One signer as the request keeps them (synced to the laptops: no email, no DocuSeal id). */
export interface SignoffSigner {
  readonly person_id: string;
  readonly name: string;
  readonly status: SignerStatus;
  readonly completed_at: string | null;
  readonly declined_at: string | null;
  readonly decline_reason: string | null;
}

/** What Teslyn keeps of a DocuSeal submission. */
export interface SubmissionState {
  readonly submissionId: number;
  readonly state: SignoffState;
  readonly completedAt: string | null;
  readonly signers: readonly (SignoffSigner & { readonly submitter_id: number | null })[];
}

const SIGNER_STATUSES: readonly SignerStatus[] = ['awaiting', 'sent', 'opened', 'completed', 'declined'];

const isoOrNull = (v: unknown): string | null =>
  typeof v === 'string' && v && !Number.isNaN(Date.parse(v)) ? new Date(v).toISOString() : null;

/**
 * Teslyn's state of a submission from DocuSeal's answer: `POST /submissions/pdf` (an object with `submitters`; older
 * accounts answer the submitters as an array, each with `submission_id`) or `GET /submissions/{id}`. Each signer is
 * matched to the person sent by `external_id` (the person id), else by role. Null when the answer is not a submission.
 */
export function submissionStateFrom(
  answer: unknown,
  sent: readonly { readonly person_id: string; readonly name: string }[],
): SubmissionState | null {
  let id: number | null = null;
  let status: unknown = null;
  let completedAt: unknown = null;
  let archivedAt: unknown = null;
  let submitters: unknown[] = [];
  if (Array.isArray(answer)) {
    submitters = answer;
    const first = answer[0] as { submission_id?: unknown } | undefined;
    id = typeof first?.submission_id === 'number' ? first.submission_id : null;
  } else if (answer && typeof answer === 'object') {
    const a = answer as Record<string, unknown>;
    id = typeof a['id'] === 'number' ? (a['id'] as number) : null;
    status = a['status'];
    completedAt = a['completed_at'];
    archivedAt = a['archived_at'];
    submitters = Array.isArray(a['submitters']) ? (a['submitters'] as unknown[]) : [];
  }
  if (id === null || !Number.isInteger(id)) return null;
  const subs = submitters.filter((s): s is Record<string, unknown> => !!s && typeof s === 'object');
  const signers = sent.map((p, i) => {
    const s =
      subs.find((x) => x['external_id'] === p.person_id) ??
      subs.find((x) => x['role'] === docusealRole(i + 1)) ??
      null;
    const st =
      s && SIGNER_STATUSES.includes(s['status'] as SignerStatus) ? (s['status'] as SignerStatus) : 'awaiting';
    return {
      person_id: p.person_id,
      name: p.name,
      status: st,
      completed_at: s ? isoOrNull(s['completed_at']) : null,
      declined_at: s ? isoOrNull(s['declined_at']) : null,
      decline_reason:
        s && typeof s['decline_reason'] === 'string' && s['decline_reason']
          ? (s['decline_reason'] as string).slice(0, 500)
          : null,
      submitter_id: s && typeof s['id'] === 'number' ? (s['id'] as number) : null,
    };
  });
  let state: SignoffState;
  if (status === 'completed' || (signers.length > 0 && signers.every((s) => s.status === 'completed')))
    state = 'completed';
  else if (status === 'declined' || signers.some((s) => s.status === 'declined')) state = 'declined';
  else if (status === 'expired') state = 'expired';
  else if (isoOrNull(archivedAt)) state = 'withdrawn';
  else state = 'sent';
  const lastSigned = signers
    .map((s) => s.completed_at)
    .filter((x): x is string => !!x)
    .sort()
    .pop();
  return {
    submissionId: id,
    state,
    completedAt: state === 'completed' ? (isoOrNull(completedAt) ?? lastSigned ?? null) : null,
    signers,
  };
}

/** The signed documents' addresses from `GET /submissions/{id}/documents?merge=true`. */
export function submissionDocumentUrls(answer: unknown): string[] {
  const docs =
    answer && typeof answer === 'object' && Array.isArray((answer as { documents?: unknown }).documents)
      ? (answer as { documents: unknown[] }).documents
      : [];
  return docs
    .map((d) => (d && typeof d === 'object' ? (d as { url?: unknown }).url : null))
    .filter((u): u is string => typeof u === 'string' && docusealFileUrlAllowed(u));
}

/** The audit log's address of a `GET /submissions/{id}` answer. */
export function submissionAuditLogUrl(answer: unknown): string | null {
  const u =
    answer && typeof answer === 'object' ? (answer as { audit_log_url?: unknown }).audit_log_url : null;
  return typeof u === 'string' && docusealFileUrlAllowed(u) ? u : null;
}

/** Only DocuSeal's own HTTPS addresses are downloaded (no other server is ever asked through the function). */
export function docusealFileUrlAllowed(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      u.protocol === 'https:' &&
      !u.username &&
      !u.password &&
      (u.port === '' || u.port === '443') &&
      /(^|\.)docuseal\.(com|eu|co)$/i.test(u.hostname)
    );
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------------------------------------ webhook

export interface DocusealEvent {
  readonly eventType: string;
  readonly submissionId: number;
}

/** The events Teslyn follows (DocuSeal, "Form webhook" and "Submission webhook"). */
export const DOCUSEAL_EVENTS = [
  'form.viewed',
  'form.started',
  'form.completed',
  'form.declined',
  'submission.created',
  'submission.completed',
  'submission.expired',
  'submission.archived',
] as const;

/**
 * Which submission a notification is about: `data.submission.id` for the form.* events (a submitter's), `data.id`
 * for the submission.* events. Null for anything else. The state in the body is never trusted: the function asks
 * DocuSeal with the organisation's own key.
 */
export function parseDocusealEvent(body: unknown): DocusealEvent | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as { event_type?: unknown; data?: unknown };
  const type = typeof b.event_type === 'string' ? b.event_type : '';
  if (!(DOCUSEAL_EVENTS as readonly string[]).includes(type)) return null;
  const data = b.data && typeof b.data === 'object' ? (b.data as Record<string, unknown>) : null;
  if (!data) return null;
  let id: unknown;
  if (type.startsWith('form.')) {
    const sub = data['submission'];
    id = sub && typeof sub === 'object' ? (sub as { id?: unknown }).id : data['submission_id'];
  } else id = data['id'];
  return typeof id === 'number' && Number.isInteger(id) && id > 0
    ? { eventType: type, submissionId: id }
    : null;
}

/** Compare the webhook's secret in constant time (the lengths may differ: never an early return on content). */
export function secretMatches(given: string | null, expected: string | undefined): boolean {
  if (!given || !expected || expected.length < 16) return false;
  const a = new TextEncoder().encode(given);
  const b = new TextEncoder().encode(expected);
  let diff = a.length ^ b.length;
  for (let i = 0; i < Math.max(a.length, b.length); i++) diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diff === 0;
}

// ------------------------------------------------------------------------------------------------ DocuSeal refusals

/** Teslyn's words for a DocuSeal answer that is not a success; DocuSeal's own message only for 422 (what it checked). */
export function docusealFailureWords(status: number, body: unknown): string {
  if (status === 401 || status === 403)
    return 'DocuSeal refused the organisation’s API key. A tenant admin replaces it in Teslyn Cloud, Integrations.';
  if (status === 422 || status === 400) {
    const m =
      body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : '';
    return m
      ? `DocuSeal did not accept the document: ${m.slice(0, 300)}`
      : 'DocuSeal did not accept the document.';
  }
  if (status === 404) return 'DocuSeal does not know this submission any more.';
  if (status === 429) return 'DocuSeal asked Teslyn to wait. Try again in a few minutes.';
  return 'DocuSeal could not be reached. Try again shortly.';
}

// ------------------------------------------------------------------------------------------------ words

/** The state of a request in words, for Teslyn Field and Teslyn Cloud. */
export function signoffStateWords(state: SignoffState): string {
  switch (state) {
    case 'sending':
      return 'Sending to DocuSeal';
    case 'sent':
      return 'Waiting for signatures';
    case 'completed':
      return 'Signed by everyone';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
    case 'withdrawn':
      return 'Withdrawn';
    case 'failed':
      return 'Not sent';
  }
}

/** A request state's QC-style tone: pass once signed, fail when declined or not sent, warn while waiting. */
export function signoffTone(state: SignoffState): 'pass' | 'warn' | 'fail' {
  if (state === 'completed') return 'pass';
  if (state === 'declined' || state === 'failed' || state === 'expired') return 'fail';
  return 'warn';
}

/** A signer's state in words. */
export function signerStatusWords(
  s: Pick<SignoffSigner, 'status' | 'completed_at' | 'decline_reason'>,
): string {
  switch (s.status) {
    case 'awaiting':
      return 'Waiting';
    case 'sent':
      return 'Email sent';
    case 'opened':
      return 'Opened';
    case 'completed':
      return s.completed_at ? `Signed ${signoffDate(s.completed_at)}` : 'Signed';
    case 'declined':
      return s.decline_reason ? `Declined: ${s.decline_reason}` : 'Declined';
  }
}

/** A date as the sign-off pages print it (dd/mm/yyyy, Australian order, in Perth time where Thomson works). */
export function signoffDate(iso: string, timeZone = 'Australia/Perth'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(d);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')}`;
}

/** Whether a request still counts: a new send of the same revision answers it instead of making another. */
export const signoffOpen = (state: SignoffState): boolean =>
  state === 'sending' || state === 'sent' || state === 'completed';
